import { useEffect, useRef } from 'react';
import { getAnalytics, logEvent, isSupported } from 'firebase/analytics';
import { useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { debounce } from 'lodash';

// Configuration
const DEBUG_MODE = process.env.NODE_ENV !== 'production';
const EXCLUDED_IPS = ['192.168.86.250'];
const INTERACTION_IDLE_THRESHOLD = 30000;
const SLOW_LOAD_THRESHOLD = 3000;
const FPS_THRESHOLD = 30;
const RAGE_CLICK_THRESHOLD = 3;
const RAGE_CLICK_TIMEOUT = 2000;

const EVENT_SAMPLING_RATES = {
  'scroll_depth': 0.1,
  'user_interaction': 0.5,
  'performance_metrics': 0.2,
  'engagement_time': 1.0
};

// Event queue and batch processing
const eventQueue = [];
const processBatchedEvents = debounce(() => {
  while (eventQueue.length) {
    const event = eventQueue.shift();
    trackEvent(event.name, event.params);
  }
}, 1000);

const determineTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer;
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  // If UTM parameters exist, use them
  if (utmSource) {
    return {
      source: utmSource,
      medium: utmMedium || 'none',
      campaign: utmCampaign || 'none',
      traffic_type: 'attributed'
    };
  }

  // Check referrer
  if (referrer) {
    const referrerDomain = new URL(referrer).hostname;
    
    // Known referrer patterns
    const referrerPatterns = {
      'google': { source: 'google', medium: 'organic' },
      'bing': { source: 'bing', medium: 'organic' },
      't.co': { source: 'twitter', medium: 'social' },
      'facebook.com': { source: 'facebook', medium: 'social' },
      'instagram.com': { source: 'instagram', medium: 'social' }
    };

    for (const [pattern, attribution] of Object.entries(referrerPatterns)) {
      if (referrerDomain.includes(pattern)) {
        return {
          ...attribution,
          campaign: 'none',
          traffic_type: 'referred'
        };
      }
    }

    // Unknown referrer
    return {
      source: referrerDomain,
      medium: 'referral',
      campaign: 'none',
      traffic_type: 'referred'
    };
  }

  // Direct traffic
  return {
    source: 'direct',
    medium: 'none',
    campaign: 'none',
    traffic_type: 'direct'
  };
};

const shouldSampleEvent = (eventName) => {
  return Math.random() < (EVENT_SAMPLING_RATES[eventName] || 1);
};

const validateEvent = (eventName, params) => {
  const maxParamLength = 100;
  return {
    eventName: eventName.slice(0, 40),
    params: Object.fromEntries(
      Object.entries(params).map(([k, v]) => [
        k.slice(0, 40),
        String(v).slice(0, maxParamLength)
      ])
    )
  };
};

const shouldTrack = async () => {
  try {
    if (window.location.hostname === 'localhost') {
      return !EXCLUDED_IPS.includes('192.168.86.250');
    }
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return !EXCLUDED_IPS.includes(data.ip);
  } catch (error) {
    console.error('Error checking IP:', error);
    return false;
  }
};

export const trackEvent = async (eventName, eventParams = {}) => {
  try {
    if (!(await shouldTrack())) {
      if (DEBUG_MODE) console.log('Analytics tracking skipped for excluded IP');
      return;
    }

    if (!shouldSampleEvent(eventName)) return;

    const { eventName: validatedName, params: validatedParams } = validateEvent(eventName, eventParams);
    const analytics = getAnalytics();
    const auth = getAuth();
    
    // Get stored initial source or determine current source
    const trafficSource = JSON.parse(sessionStorage.getItem('initial_source')) || determineTrafficSource();
    
    const enhancedParams = {
      ...validatedParams,
      ...trafficSource,
      user_type: auth.currentUser ? 'authenticated' : 'guest',
      user_id: auth.currentUser?.uid || 'anonymous',
      timestamp: new Date().toISOString(),
      session_id: sessionStorage.getItem('session_id') || 'unknown',
      browser: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      device_type: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) 
        ? 'mobile' 
        : 'desktop',
      connection_type: navigator.connection?.effectiveType || 'unknown',
      connection_speed: navigator.connection?.downlink 
        ? `${navigator.connection.downlink}Mbps` 
        : 'unknown',
      connection_rtt: navigator.connection?.rtt || 'unknown',
      connection_saveData: navigator.connection?.saveData || false,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      page_referrer: document.referrer
    };

    await logEvent(analytics, validatedName, enhancedParams);

    if (DEBUG_MODE) {
      console.log('Analytics Event Tracked:', {
        eventName: validatedName,
        params: enhancedParams
      });
    }
  } catch (error) {
    console.error('Analytics Error:', error);
    try {
      const analytics = getAnalytics();
      await logEvent(analytics, 'analytics_error', {
        error_message: error.message,
        error_stack: error.stack,
        failed_event: eventName
      });
    } catch (e) {
      console.error('Failed to track analytics error:', e);
    }
  }
};

const Analytics = () => {
  const location = useLocation();
  const auth = getAuth();
  
  const pageStartTimeRef = useRef(Date.now());
  const lastActiveTimeRef = useRef(Date.now());
  const totalEngagementTimeRef = useRef(0);
  const isEngagedRef = useRef(true);
  const lastTrackTimeRef = useRef(Date.now());
  const scrollDepthRef = useRef(0);
  const analyticsRef = useRef({ isSupported: true });
  const fpsMetricsRef = useRef({ frames: 0, lastTime: performance.now() });
  const resourceTimingRef = useRef(new Set());
  const errorCountRef = useRef(0);
  
  const formInteractionsRef = useRef({
    startTime: null,
    fieldsFilled: new Set(),
    totalFields: 0,
    submits: 0,
    errors: 0
  });

  const trackTimeOnPage = () => {
    if (!isEngagedRef.current || document.hidden) return;
    
    const currentTime = Date.now();
    const timeSinceLastActive = currentTime - lastActiveTimeRef.current;
    
    if (timeSinceLastActive < INTERACTION_IDLE_THRESHOLD) {
      totalEngagementTimeRef.current += timeSinceLastActive;
    }
    
    lastActiveTimeRef.current = currentTime;
    
    const engagementSeconds = Math.round(totalEngagementTimeRef.current / 1000);
    const timeSinceLastTrack = Math.round((currentTime - lastTrackTimeRef.current) / 1000);
    
    if (engagementSeconds > 0 && timeSinceLastTrack >= 5) {
      eventQueue.push({
        name: 'engagement_time',
        params: {
          total_seconds: engagementSeconds,
          increment_seconds: timeSinceLastTrack,
          page: location.pathname,
          is_bounce: engagementSeconds < 30,
          engagement_state: isEngagedRef.current ? 'engaged' : 'passive',
          scroll_depth: scrollDepthRef.current
        }
      });
      
      processBatchedEvents();
      lastTrackTimeRef.current = currentTime;
    }
  };

  const trackPerformanceMetrics = () => {
    if (!window.performance || !window.performance.timing) return;
    
    const timing = performance.timing;
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    
    eventQueue.push({
      name: 'performance_metrics',
      params: {
        page_load_time: timing.loadEventEnd - timing.navigationStart,
        dom_load_time: timing.domContentLoadedEventEnd - timing.navigationStart,
        ttfb: timing.responseStart - timing.navigationStart,
        fps: Math.round(fpsMetricsRef.current.frames * 1000 / 
          (performance.now() - fpsMetricsRef.current.lastTime)),
        resources_count: performance.getEntriesByType('resource').length,
        is_slow_load: (timing.loadEventEnd - timing.navigationStart) > SLOW_LOAD_THRESHOLD
      }
    });
    
    processBatchedEvents();
    fpsMetricsRef.current = { frames: 0, lastTime: performance.now() };
  };

  useEffect(() => {
    const initializeAnalytics = async () => {
      const supported = await isSupported();
      analyticsRef.current.isSupported = supported;
      
      if (!supported || !(await shouldTrack())) {
        if (DEBUG_MODE) console.log('Analytics disabled');
        return;
      }

      // Set initial source if not already set
      if (!sessionStorage.getItem('initial_source')) {
        const trafficSource = determineTrafficSource();
        sessionStorage.setItem('initial_source', JSON.stringify(trafficSource));
      }

      if (!sessionStorage.getItem('session_id')) {
        sessionStorage.setItem(
          'session_id', 
          `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        );
      }

      eventQueue.push({
        name: 'page_view',
        params: { landing_page: sessionStorage.getItem('landing_page') || 'true' }
      });
      processBatchedEvents();

      sessionStorage.setItem('landing_page', 'false');
      trackPerformanceMetrics();
    };

    initializeAnalytics();

    const trackActivity = () => {
      lastActiveTimeRef.current = Date.now();
      isEngagedRef.current = true;
    };

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    activityEvents.forEach(event => {
      window.addEventListener(event, trackActivity);
    });

    const visibilityChangeHandler = () => {
      if (document.hidden) {
        trackTimeOnPage();
        isEngagedRef.current = false;
      } else {
        lastActiveTimeRef.current = Date.now();
        isEngagedRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    const engagementInterval = setInterval(trackTimeOnPage, 5000);
    const performanceInterval = setInterval(trackPerformanceMetrics, 30000);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
      clearInterval(engagementInterval);
      clearInterval(performanceInterval);
      
      trackTimeOnPage();
      eventQueue.push({
        name: 'page_exit',
        params: {
          total_engagement_time: Math.round(totalEngagementTimeRef.current / 1000),
          scroll_depth: scrollDepthRef.current,
          form_completions: formInteractionsRef.current.submits
        }
      });
      processBatchedEvents();
    };
  }, [location]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        eventQueue.push({
          name: 'user_login',
          params: {
            user_id: user.uid,
            login_method: user.providerData[0]?.providerId || 'unknown',
            is_new_user: user.metadata.creationTime === user.metadata.lastSignInTime
          }
        });
      } else {
        eventQueue.push({ name: 'user_logout', params: {} });
      }
      processBatchedEvents();
    });

    return () => unsubscribe();
  }, [auth]);

  return null;
};

export default Analytics;